import React, { useState, useEffect, useCallback } from 'react';
import { Button, Alert, Spinner, Badge } from 'react-bootstrap';
import moment from 'moment';
import axios from 'axios';
import Utils from '../../common/Utils';

const MINUTE_STEPS = [0, 15, 30, 45];
const MAX_HOURS = 12;

const toDecimal = (h, m) => Math.round((h + m / 60) * 4) / 4;

// ── hh:mm stepper — visually "− 1 : 30 (hh:mm)" / "+ 1 : 45 (hh:mm)" ───
const HourMinuteInput = ({ hours, minutes, onChange, accent }) => (
  <div className="d-flex align-items-center gap-2">
    <div
      className="d-flex align-items-center justify-content-center fw-bold"
      style={{ width: 28, height: 34, borderRadius: 6, background: accent, color: '#fff', fontSize: 18 }}
    >
      {accent === '#dc3545' ? '−' : '+'}
    </div>
    <select
      className="form-select form-select-sm text-center"
      style={{ width: 64 }}
      value={hours}
      onChange={(e) => onChange(parseInt(e.target.value), minutes)}
    >
      {Array.from({ length: MAX_HOURS + 1 }, (_, i) => i).map((h) => (
        <option key={h} value={h}>{h}</option>
      ))}
    </select>
    <span className="fw-bold">:</span>
    <select
      className="form-select form-select-sm text-center"
      style={{ width: 70 }}
      value={minutes}
      onChange={(e) => onChange(hours, parseInt(e.target.value))}
    >
      {MINUTE_STEPS.map((m) => (
        <option key={m} value={m}>{m < 10 ? '0' + m : m}</option>
      ))}
    </select>
    <span className="small text-muted">(hh : mm)</span>
  </div>
);

// Reuses the existing attendence/mark (ontime=0) endpoint — the same one the
// Differed Hours form (Log Backdated Hours) already submits to.
const DailyDeclaration = ({ checkedOut, onSuccess, onClose, isModal, refreshTrigger }) => {
  const [declType, setDeclType] = useState(null); // 'minus' | 'overtime' | 'lieu'
  const [minusH, setMinusH] = useState(1);
  const [minusM, setMinusM] = useState(0);
  const [otH, setOtH] = useState(1);
  const [otM, setOtM] = useState(0);
  const [lieuSign, setLieuSign] = useState('add'); // 'add' | 'take'
  const [lieuH, setLieuH] = useState(1);
  const [lieuM, setLieuM] = useState(0);
  const [declReason, setDeclReason] = useState('');
  const [declSubmitting, setDeclSubmitting] = useState(false);
  const [declResult, setDeclResult] = useState(null); // {type, text}
  const [submittedAid, setSubmittedAid] = useState(0);
  const [approvalStatus, setApprovalStatus] = useState(null); // 'pending' | 'approved' | 'rejected'
  const [showAdvance, setShowAdvance] = useState(false);
  const [standardConfirmed, setStandardConfirmed] = useState(false);

  // Same approved/approved_at semantics MySummary.js already uses to badge
  // Differed Hours entries — just reused here via the existing attendence/fetchlogs
  const refreshApprovalStatus = useCallback(async (aid) => {
    if (!aid) return;
    try {
      const res = await axios.get(Utils.apiUrl('attendence/fetchlogs'), Utils.apiHeaders());
      const data = Utils.parseApiResponse(res);
      const rec = (data?.data?.logs || []).find((r) => parseInt(r.id) === parseInt(aid));
      if (rec) {
        if (rec.approved === '1' || rec.approved === 1) setApprovalStatus('approved');
        else if (rec.approved_at) setApprovalStatus('rejected');
        else setApprovalStatus('pending');
      }
    } catch { /* keep last known status */ }
  }, []);

  // Poll while pending so an in-page manager approval reflects without reload
  useEffect(() => {
    if (!submittedAid || approvalStatus !== 'pending') return;
    const id = setInterval(() => refreshApprovalStatus(submittedAid), 20000);
    return () => clearInterval(id);
  }, [submittedAid, approvalStatus, refreshApprovalStatus]);

  // On mount / after check-out / after refreshTrigger, restore today's declaration from the backend
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(Utils.apiUrl('attendence/fetchlogs'), Utils.apiHeaders());
        const data = Utils.parseApiResponse(res);
        const today = moment().format('YYYY-MM-DD');
        const rec = (data?.data?.logs || []).find(
          (r) => (r.ontime === '0' || r.ontime === 0) && (r.diftype === 'paid' || r.diftype === 'unpaid' || r.diftype === 'lieu') && moment(r.login).format('YYYY-MM-DD') === today
        );
        if (rec && !cancelled) {
          const dur = parseFloat(rec.duration);
          const absDur = Math.abs(dur);
          const h = Math.floor(absDur);
          const m = Math.round((absDur - h) * 60);
          if (rec.diftype === 'paid') {
            setOtH(h); setOtM(m);
            setDeclType('overtime');
          } else if (rec.diftype === 'lieu') {
            setLieuH(h); setLieuM(m);
            setLieuSign(dur < 0 ? 'take' : 'add');
            setDeclType('lieu');
          } else {
            setMinusH(h); setMinusM(m);
            setDeclType('minus');
          }
          setSubmittedAid(rec.id);
          if (rec.approved === '1' || rec.approved === 1) setApprovalStatus('approved');
          else if (rec.approved_at) setApprovalStatus('rejected');
          else setApprovalStatus('pending');
          setDeclResult({ type: 'success', text: 'Declaration is submitted and awaits manager approval (if applicable).' });
        } else if (!cancelled) {
          setDeclResult(null);
          setSubmittedAid(0);
          setApprovalStatus(null);
        }
      } catch {
        if (!cancelled) {
          setDeclResult(null);
          setSubmittedAid(0);
          setApprovalStatus(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [checkedOut, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteDeclaration = async () => {
    if (!submittedAid) return;
    if (window.confirm("Are you sure you want to remove this declaration?")) {
      try {
        const res = await axios.post(Utils.apiUrl("attendence/deleteattn"), `id=${submittedAid}`, Utils.apiHeaders());
        const data = Utils.parseApiResponse(res);
        if (data.success) {
          setDeclResult(null);
          setSubmittedAid(0);
          setApprovalStatus(null);
          setDeclType(null);
          setDeclReason('');
          if (onSuccess) onSuccess(0);
        } else {
          window.alert(data.message || "Could not delete declaration.");
        }
      } catch {
        window.alert("Network error deleting declaration.");
      }
    }
  };

  const submitDeclaration = async () => {
    if (!declType) return;
    if (!declReason.trim()) {
      window.alert('Please enter the Reason!');
      return;
    }
    let duration, diftype, signedDuration;
    if (declType === 'overtime') {
      duration = toDecimal(otH, otM);
      diftype = 'paid';
      signedDuration = duration;
    } else if (declType === 'lieu') {
      duration = toDecimal(lieuH, lieuM);
      diftype = 'lieu';
      signedDuration = lieuSign === 'take' ? -duration : duration;
    } else {
      duration = toDecimal(minusH, minusM);
      diftype = 'unpaid';
      signedDuration = -duration;
    }
    if (duration === 0) {
      window.alert('Please enter hours/minutes!');
      return;
    }
    setDeclSubmitting(true);
    const data = `&ts=${moment().format('YYYY-MM-DD HH:mm:ss')}&ontime=0&diftype=${diftype}&duration=${signedDuration}&reason=${encodeURIComponent(declReason)}&member=`;
    try {
      const res = await axios.post(Utils.apiUrl('attendence/mark'), data, Utils.apiHeaders());
      const resp = Utils.parseApiResponse(res);
      if (resp.success) {
        setDeclResult({ type: 'success', text: 'Declaration is submitted and awaits manager approval (if applicable).' });
        setSubmittedAid(resp.aid);
        refreshApprovalStatus(resp.aid);
        if (onSuccess) onSuccess(resp.aid);
      } else {
        setDeclResult({ type: 'danger', text: resp.message || 'Could not submit declaration.' });
      }
    } catch {
      setDeclResult({ type: 'danger', text: 'Network error. Please try again.' });
    } finally {
      setDeclSubmitting(false);
    }
  };

  // Render 1: Submitted Result View
  if (declResult && declResult.type === 'success') {
    let duration, label;
    if (declType === 'overtime') {
      duration = toDecimal(otH, otM);
      label = 'Overtime Hours';
    } else if (declType === 'lieu') {
      duration = toDecimal(lieuH, lieuM);
      label = `Time In Lieu (${lieuSign === 'take' ? 'Taken' : 'Added'})`;
    } else {
      duration = toDecimal(minusH, minusM);
      label = 'Minus Hours';
    }

    return (
      <div className="text-center py-2">
        <div
          className="mx-auto"
          style={{ maxWidth: 320, background: '#eef3ff', border: '1px solid #dbe6ff', borderRadius: 14, padding: '20px' }}
        >
          <div
            className="mx-auto mb-3 d-flex align-items-center justify-content-center"
            style={{ width: 44, height: 44, borderRadius: '50%', background: '#198754' }}
          >
            <i className="bi bi-check-lg text-white fs-4" />
          </div>
          <div className="fw-bold text-dark">Declaration submitted</div>
          <div className="text-muted small mt-1">{label} &middot; {duration.toFixed(2)} Hrs.</div>
          <div className="mt-2">
            {approvalStatus === 'approved' && <Badge bg="success">Approved</Badge>}
            {approvalStatus === 'rejected' && <Badge bg="danger">Rejected</Badge>}
            {(approvalStatus === 'pending' || !approvalStatus) && <Badge bg="warning" text="dark">Pending Approval</Badge>}
          </div>
          <div className="text-muted small mt-2">{declResult.text}</div>
          <div className="mt-3 pt-2 border-top d-flex justify-content-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline-primary"
              className="rounded-pill px-3 fw-semibold"
              onClick={() => {
                setDeclResult(null);
              }}
            >
              ✏️ Edit / Change
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              className="rounded-pill px-3 fw-semibold"
              onClick={deleteDeclaration}
            >
              🗑️ Remove
            </Button>
            {onClose && (
              <Button
                size="sm"
                variant="primary"
                className="rounded-pill px-3 fw-semibold"
                onClick={onClose}
              >
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render 2: Error View
  if (declResult && declResult.type === 'danger') {
    return (
      <div className="text-center py-2">
        <Alert variant="danger" className="py-2 small mb-2">{declResult.text}</Alert>
        <Button size="sm" variant="outline-secondary" onClick={() => setDeclResult(null)}>Try Again</Button>
      </div>
    );
  }

  // Render 3: Standard Shift Confirmed
  if (standardConfirmed && !isModal) {
    return (
      <div className="text-center py-3">
        <div className="mx-auto p-3 bg-light rounded-3 border" style={{ maxWidth: 320 }}>
          <i className="bi bi-check-circle-fill text-success fs-3 mb-2 d-block" />
          <div className="fw-bold text-dark mb-1">Standard Shift Confirmed</div>
          <div className="text-muted small mb-2">No overtime or minus hours declared for today.</div>
          <Button
            size="sm"
            variant="outline-primary"
            className="rounded-pill px-3 py-1 fw-semibold mt-1"
            style={{ fontSize: "12px" }}
            onClick={() => setStandardConfirmed(false)}
          >
            ➕ Declare Extra / Minus Hours
          </Button>
        </div>
      </div>
    );
  }

  // Render 4: Shift Not Completed Yet (and not inside modal)
  if (!checkedOut && !showAdvance && !isModal) {
    return (
      <div className="text-center py-4 px-2">
        <div className="text-muted mb-2" style={{ fontSize: "30px" }}>🕒</div>
        <div className="fw-bold text-dark mb-1" style={{ fontSize: "14px" }}>
          End-of-Day Declaration
        </div>
        <div className="text-muted small mb-3" style={{ maxWidth: 280, margin: "0 auto" }}>
          Used at the end of your shift if you have Overtime, Minus hours, or Time In Lieu to declare.
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          className="rounded-pill px-3 py-1 fw-semibold"
          style={{ fontSize: "12px" }}
          onClick={() => setShowAdvance(true)}
        >
          Declare in advance (optional)
        </Button>
      </div>
    );
  }

  // Render 5: Interactive Declaration Form
  return (
    <div className="px-1">
      <div className="text-center small text-muted mb-3">
        How should today's attendance be treated?<br />Select an option, add hours and reason.
      </div>

      {/* Minus Hours */}
      <div
        className="p-3 mb-3 shadow-sm"
        style={{ background: '#fdeeee', border: `2px solid ${declType === 'minus' ? '#dc3545' : '#f3d3d3'}`, borderRadius: 12, cursor: 'pointer' }}
        onClick={() => setDeclType('minus')}
      >
        <div className="d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-dash-circle-fill text-danger fs-5" />
          <span className="fw-bold text-danger">MINUS HOURS</span>
          <span className="small text-muted">(Deduction)</span>
        </div>
        {declType === 'minus' && (
          <>
            <div className="small text-muted mb-1">Enter Hours (negative)</div>
            <HourMinuteInput hours={minusH} minutes={minusM} accent="#dc3545" onChange={(h, m) => { setMinusH(h); setMinusM(m); }} />
            <div className="small text-muted mt-2 mb-1">Reason (required)</div>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Left early for medical appointment"
              value={declReason}
              onChange={(e) => setDeclReason(e.target.value)}
            />
          </>
        )}
      </div>

      {/* Overtime Hours */}
      <div
        className="p-3 mb-3 shadow-sm"
        style={{ background: '#eaf1ff', border: `2px solid ${declType === 'overtime' ? '#0d6efd' : '#cfe0ff'}`, borderRadius: 12, cursor: 'pointer' }}
        onClick={() => setDeclType('overtime')}
      >
        <div className="d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-plus-circle-fill text-primary fs-5" />
          <span className="fw-bold text-primary">OVERTIME HOURS</span>
          <span className="small text-muted">(Paid)</span>
        </div>
        {declType === 'overtime' && (
          <>
            <div className="small text-muted mb-1">Enter Hours (positive)</div>
            <HourMinuteInput hours={otH} minutes={otM} accent="#0d6efd" onChange={(h, m) => { setOtH(h); setOtM(m); }} />
            <div className="small text-muted mt-2 mb-1">Reason (required)</div>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Stayed late for emergency patient"
              value={declReason}
              onChange={(e) => setDeclReason(e.target.value)}
            />
          </>
        )}
      </div>

      {/* Time In Lieu */}
      <div
        className="p-3 mb-3 shadow-sm"
        style={{ background: '#f3eefd', border: `2px solid ${declType === 'lieu' ? '#6f42c1' : '#ddd0f5'}`, borderRadius: 12, cursor: 'pointer' }}
        onClick={() => setDeclType('lieu')}
      >
        <div className="d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-arrow-repeat text-purple fs-5" style={{ color: '#6f42c1' }} />
          <span className="fw-bold" style={{ color: '#6f42c1' }}>TIME IN LIEU</span>
          <span className="small text-muted">(TOIL)</span>
        </div>
        {declType === 'lieu' && (
          <>
            <div className="d-flex gap-2 mb-2">
              <Button
                size="sm"
                variant={lieuSign === 'add' ? 'dark' : 'outline-secondary'}
                onClick={(e) => { e.stopPropagation(); setLieuSign('add'); }}
              >
                + Add (Earned)
              </Button>
              <Button
                size="sm"
                variant={lieuSign === 'take' ? 'dark' : 'outline-secondary'}
                onClick={(e) => { e.stopPropagation(); setLieuSign('take'); }}
              >
                − Take (Used)
              </Button>
            </div>
            <div className="small text-muted mb-1">Enter Hours</div>
            <HourMinuteInput hours={lieuH} minutes={lieuM} accent={lieuSign === 'take' ? '#dc3545' : '#6f42c1'} onChange={(h, m) => { setLieuH(h); setLieuM(m); }} />
            <div className="small text-muted mt-2 mb-1">Reason (required)</div>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Reason for Time In Lieu"
              value={declReason}
              onChange={(e) => setDeclReason(e.target.value)}
            />
          </>
        )}
      </div>

      {/* Action buttons */}
      {(declType === 'minus' || declType === 'overtime' || declType === 'lieu') ? (
        <div className="text-center mt-3 d-flex gap-2">
          <Button
            variant="light"
            size="sm"
            className="w-25 border rounded-3 fw-semibold"
            onClick={() => setDeclType(null)}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            className="fw-bold flex-grow-1"
            style={{ borderRadius: 8, padding: '8px 0' }}
            disabled={declSubmitting}
            onClick={submitDeclaration}
          >
            {declSubmitting ? <Spinner animation="border" size="sm" /> : 'SUBMIT DECLARATION'}
          </Button>
        </div>
      ) : (
        <div className="text-center mt-3 pt-2 border-top">
          <div className="text-muted small mb-2">Worked regular shift without extra or minus hours?</div>
          <Button
            variant="outline-success"
            size="sm"
            className="rounded-pill px-4 py-1 fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
            style={{ fontSize: "12.5px" }}
            onClick={() => {
              if (onClose) onClose();
              setStandardConfirmed(true);
            }}
          >
            <i className="bi bi-check-circle-fill text-success" />
            Standard Shift (No Adjustments Needed)
          </Button>
        </div>
      )}
    </div>
  );
};

export default DailyDeclaration;

